/* eslint-disable */
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/SwipeableDrawer';
import withWidth, { isWidthDown, isWidthUp } from '@material-ui/core/withWidth';
import SettingsIcon from '@material-ui/icons/Settings';
import cn from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import withEntries from '../../Contexts/Entries/WithEntries.context';
import { ENTRY_PORTAL_CUSTOMER_NAME } from '../../Entries/PortalCustomer/PortalCustomer.entry.constants';
import { createSettingsPageLink, isSettingsPage } from '../../Pages/Settings/Settings.page.route';
import sidebarOpenSelector from '../../State/Store/Sidebar/Selectors/SidebarOpen.selector';
import sidebarActionCreators from '../../State/Store/Sidebar/Sidebar.actions';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import I18n from '../I18n/I18n.component';
import './Sidebar.component.i18n';
import SidebarPropTypes from './Sidebar.component.props';
import sidebarStyles from './Sidebar.component.styles';

/**
 * SidebarComponent
 * @export
 * @class SidebarComponent
 * @extends {React.Component<SidebarComponentComposedProps, SidebarComponentState>}
 */
export class SidebarComponent extends React.Component {
  /**
   * The `props` to seed when any specific prop receives an `undefined` value. **NOT** a `null` value
   * @static
   * @type {Partial<SidebarComponentProps>}
   * @memberof SidebarComponent
   */
  static defaultProps = {
    anchor: `left`,
    open: false,
  };

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`Sidebar`}
   * @memberof SidebarComponent
   */
  static displayName = `Sidebar`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof SidebarComponent
   */
  static propTypes = SidebarPropTypes;

  /**
   * Data specific to the `<SidebarComponent />` instance
   * @type {SidebarComponentState}
   * @memberof SidebarComponent
   */
  state = {};

  /**
   * componentDidMount
   * @returns {void} void
   * @memberof SidebarComponent
   */
  componentDidMount() {
    const { actions, width } = this.props;
    this.handleResponsiveSidebar();
    if (isWidthDown(`md`, width)) {
      actions.sidebarClose();
    }
    return;
  }

  /**
   * componentDidUpdate
   * @returns {void} void
   * @param {any} prevProps prevProps
   * @memberof SidebarComponent
   */
  componentDidUpdate(prevProps) {
    const { actions, width } = this.props;
    if (prevProps.width === width) {
      return;
    }
    this.handleResponsiveSidebar();
  }

  /**
   * Handle responsive sidebar open or close
   * @returns {void} void
   * @memberof SidebarComponent
   */
  handleResponsiveSidebar = () => {
    const { width } = this.props;
    if (isWidthUp(`lg`, width)) {
      this.handleSidebarOpen();
    }
    if (isWidthDown(`md`, width)) {
      this.handleSidebarClose();
    }
  };

  /**
   * Handle closing the sidebar on mobile
   * @returns {void} void
   * @memberof SidebarComponent
   */
  handleSidebarClose = () => {
    const { actions, anchor } = this.props;
    if (anchor === `right`) {
      actions.sidebarRightClose();
      return;
    }
    actions.sidebarClose();
  };

  /**
   * Handle opening the sidebar on mobile
   * @returns {void} void
   * @memberof SidebarComponent
   */
  handleSidebarOpen = () => {
    const { actions, anchor } = this.props;
    if (anchor === `right`) {
      actions.sidebarRightOpen();
      return;
    }
    actions.sidebarOpen();
  };

  /**
   * Determine whether or not to show the footer.
   * @returns {boolean} Whether or not to show the footer.
   * @memberof SidebarComponent
   */
  shouldRenderFooter() {
    const { entry } = this.props;
    if (entry.name === ENTRY_PORTAL_CUSTOMER_NAME) {
      return false;
    }
    return true;
  }

  /**
   * Render the page specifc content
   * @returns {React.Component} The rendered content for `<SidebarComponent />`
   * @memberof SidebarComponent
   */
  renderFooter = () => {
    const { match } = this.props;
    return (
      <List>
        <ListItem {...(isSettingsPage(match) ? {} : { button: true, component: Link, to: createSettingsPageLink() })}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>
            <I18n>Sidebar.ListItem.Settings</I18n>
          </ListItemText>
        </ListItem>
      </List>
    );
  };

  /**
   * Render the component
   * @returns {React.Component} The rendered `<SidebarComponent />`
   * @memberof SidebarComponent
   */
  render() {
    const { anchor, children, classes, open, tempSizing, permSizing } = this.props;
    return (
      <React.Fragment>
        <Hidden {...tempSizing}>
          <Drawer
            anchor={anchor}
            classes={{ paper: classes.paper }}
            className={cn(classes.root, {
              [classes.closed]: !open,
            })}
            onClose={this.handleSidebarClose}
            onOpen={() => {}}
            open={open}
            variant="temporary"
          >
            {children}
            <span className={cn(classes.spacer)} />
            {this.shouldRenderFooter() && this.renderFooter()}
          </Drawer>
        </Hidden>
        <Hidden {...permSizing}>
          <Drawer
            anchor={anchor}
            classes={{ paper: classes.paper }}
            className={cn(classes.root, {
              [classes.closed]: !open,
            })}
            onClose={this.handleSidebarClose}
            onOpen={() => {}}
            open
            variant="permanent"
          >
            {children}
            <span className={cn(classes.spacer)} />
            {this.shouldRenderFooter() && this.renderFooter()}
          </Drawer>
        </Hidden>
      </React.Fragment>
    );
  }
}

/** @type {React.ComponentClass<SidebarComponentProps>} */
const Sidebar = compose(
  withEntries,
  withRouter,
  withWidth(),
  withStyles(themeCreateInjectableStyles(sidebarStyles), { withTheme: true }),
  connect(
    (state, ownProps) => {
      const { anchor } = ownProps;
      const sideBarOpen = sidebarOpenSelector(anchor);
      return {
        open: sideBarOpen(state),
      };
    },
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...sidebarActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(SidebarComponent);

export default Sidebar;
